import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { RegistrationApiService } from '../api/RegistrationApiService';
import { DOCTOR_INFO_REFRESH_EVENT } from '../../../shared/utils/events/DoctorInfoRefreshEvent';
import { DOCTOR_LOGOUT_EVENT } from '../../../shared/utils/events/DoctorLogoutEvent';

// Event for triggering doctor info refresh from anywhere (e.g., after login)
export const DOCTOR_INFO_FORCE_REFRESH_EVENT = 'doctor-info-force-refresh';

// Helper to trigger refresh from anywhere in the app
export const triggerDoctorInfoRefresh = () => {
  window.dispatchEvent(new CustomEvent(DOCTOR_INFO_FORCE_REFRESH_EVENT));
};

export interface DoctorInfoData {
  doctor_account_info?: any;
  doctor_info?: any;
  clinic_info?: any;
  doctor_docs?: any[];
  clinic_docs?: any[];
  subscription_info?: any;
  is_junior?: boolean;
  registration_progress?: string;
  status?: string;
  documents?: {
    profile_photo?: string;
    face_verification_photo?: string;
    syndicate_id_face?: string;
    syndicate_id_back?: string;
    specialization_certificate?: string;
    [key: string]: string | undefined;
  };
}

export interface DoctorInfoContextType {
  doctorInfo: DoctorInfoData | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number;
  refreshDoctorInfo: (force?: boolean) => Promise<DoctorInfoData | null>;
  updateDoctorInfo: (partialData: Partial<DoctorInfoData>) => void;
  clearDoctorInfo: () => void;
  clearError: () => void;
}

const DoctorInfoContext = createContext<DoctorInfoContextType | undefined>(undefined);

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;
// Request timeout: 30 seconds  
const REQUEST_TIMEOUT = 30 * 1000;

export function DoctorInfoProvider({ children }: { children: React.ReactNode }) {
  const [doctorInfo, setDoctorInfo] = useState<DoctorInfoData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState(0);

  // Track in-flight requests to prevent duplicates
  const activeRequestRef = useRef<Promise<DoctorInfoData | null> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const registrationApiRef = useRef<RegistrationApiService>();

  // Initialize API service
  if (!registrationApiRef.current) {
    registrationApiRef.current = new RegistrationApiService();
  }

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearDoctorInfo = useCallback(() => {
    setDoctorInfo(null);
    setLastFetched(0);
    setError(null);

    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    activeRequestRef.current = null;
  }, []);

  const updateDoctorInfo = useCallback((partialData: Partial<DoctorInfoData>) => {
    setDoctorInfo(prev => prev ? { ...prev, ...partialData } : partialData as DoctorInfoData);
  }, []);

  const refreshDoctorInfo = useCallback(async (force: boolean = false): Promise<DoctorInfoData | null> => {
    // Check if we need to fetch
    const now = Date.now();
    const isCacheValid = (now - lastFetched) < CACHE_DURATION;

    if (!force && isCacheValid && doctorInfo) {
      return doctorInfo;
    }

    // If there's already a request in flight, wait for it and return its result
    if (activeRequestRef.current) {
      try {
        const result = await activeRequestRef.current;
        return result;
      } catch (error) {
        // Error already handled in the original request
        return null;
      }
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;


    // Create the request promise that returns the fetched data
    const requestPromise = (async (): Promise<DoctorInfoData | null> => {
      let fetchedDoctorInfo: DoctorInfoData | null = null;

      try {
        setIsLoading(true);
        setError(null);

        // Add timeout protection
        const timeoutId = setTimeout(() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
        }, REQUEST_TIMEOUT);

        const result = await registrationApiRef.current!.getMyInfo();

        clearTimeout(timeoutId);

        // Check if request was aborted
        if (signal.aborted) {
          return null;
        }

        if (result.isSuccess && result.data) {
          // The RegistrationApiService already transforms the data to the correct structure
          // So we can directly use result.data here
          const newDoctorInfo: DoctorInfoData = {
            doctor_account_info: result.data.doctor_info,
            doctor_info: result.data.doctor_info,
            clinic_info: result.data.clinic_info,
            doctor_docs: result.data.doctor_docs || [],
            clinic_docs: result.data.clinic_docs || [],
            subscription_info: result.data.subscription_info,
            is_junior: result.data.is_junior,
            registration_progress: result.data.doctor_info?.registration_progress,
            status: result.data.doctor_info?.status,
          };

          setDoctorInfo(newDoctorInfo);
          setLastFetched(now);
          setError(null);
          fetchedDoctorInfo = newDoctorInfo;

        } else {
          const errorMsg = result.error || 'Failed to fetch doctor information';
          setError(errorMsg);
        }
      } catch (err) {
        if (signal.aborted) {
          return null;
        }

        const errorMsg = err instanceof Error ? err.message : 'Network error occurred';
        setError(errorMsg);
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
        // Clean up
        activeRequestRef.current = null;
        if (abortControllerRef.current && abortControllerRef.current.signal === signal) {
          abortControllerRef.current = null;
        }
      }

      return fetchedDoctorInfo;
    })();

    // Store the active request
    activeRequestRef.current = requestPromise;

    // Wait for completion and return the result
    return await requestPromise;
  }, [doctorInfo, lastFetched]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Listen for global refresh requests (e.g. after completing a registration step)
  useEffect(() => {
    const handleRefreshEvent = () => {
      refreshDoctorInfo(true);
    };

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener(DOCTOR_INFO_REFRESH_EVENT, handleRefreshEvent);
      window.addEventListener(DOCTOR_INFO_FORCE_REFRESH_EVENT, handleRefreshEvent);
    }

    return () => {
      if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener(DOCTOR_INFO_REFRESH_EVENT, handleRefreshEvent);
        window.removeEventListener(DOCTOR_INFO_FORCE_REFRESH_EVENT, handleRefreshEvent);
      }
    };
  }, [refreshDoctorInfo]);

  // Listen for logout events to clear doctor info data
  useEffect(() => {
    const handleLogout = () => {
      console.log('🧹 [DoctorInfoContext] Clearing doctor info on logout');
      clearDoctorInfo();
    };

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener(DOCTOR_LOGOUT_EVENT, handleLogout);
    }

    return () => {
      if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener(DOCTOR_LOGOUT_EVENT, handleLogout);
      }
    };
  }, [clearDoctorInfo]);

  const contextValue: DoctorInfoContextType = {
    doctorInfo,
    isLoading,
    error,
    lastFetched,
    refreshDoctorInfo,
    updateDoctorInfo,
    clearDoctorInfo,
    clearError,
  };

  return (
    <DoctorInfoContext.Provider value={contextValue}>
      {children}
    </DoctorInfoContext.Provider>
  );
}

export function useDoctorInfo() {
  const context = useContext(DoctorInfoContext);
  if (!context) {
    throw new Error('useDoctorInfo must be used within a DoctorInfoProvider');
  }
  return context;
}

// Helper hook for common patterns
export function useDoctorInfoWithAutoRefresh(autoRefresh: boolean = true) {
  const context = useDoctorInfo();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (autoRefresh && !hasInitialized.current) {
      hasInitialized.current = true;
      // Force refresh if no data exists (e.g., after fresh login)
      const shouldForce = !context.doctorInfo;
      console.log('🔄 [useDoctorInfoWithAutoRefresh] Auto-refreshing, force:', shouldForce);
      context.refreshDoctorInfo(shouldForce);
    }
  }, [autoRefresh, context]);

  return context;
}

// Legacy aliases for backward compatibility - will be removed
export const UserInfoProvider = DoctorInfoProvider;
export const useUserInfo = useDoctorInfo;
export const useUserInfoWithAutoRefresh = useDoctorInfoWithAutoRefresh;
export type UserInfoData = DoctorInfoData;
export type UserInfoContextType = DoctorInfoContextType; 